import { useState } from "react";
import { X, Loader2, AlertCircle, CheckCircle2, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";

export const REASON_LABELS: Record<string, string> = {
  not_received: "Item / work not received",
  not_as_described: "Not as described",
  quality: "Quality issue",
  other: "Other",
};
export const disputeStatusChip = (s: string) => {
  const map: Record<string, string> = {
    open: "bg-brand-gold/15 text-[#8a5a00]", responded: "bg-navy-50 text-navy-600",
    resolved: "bg-brand-green/10 text-brand-greendark", cancelled: "bg-line text-mut",
  };
  return <span className={`chip capitalize ${map[s] || "bg-navy-50 text-navy-600"}`}>{s}</span>;
};

/** Open a dispute on a deal. */
export function DisputeModal({ deal, onClose, onDone }: {
  deal: { dealType: "order" | "contract"; dealId: string; contextLabel?: string };
  onClose: () => void; onDone: (d: any) => void;
}) {
  const [reason, setReason] = useState("not_received");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit() {
    setErr(null); setBusy(true);
    try {
      const res = await api.openDispute({ dealType: deal.dealType, dealId: deal.dealId, reason, message: message.trim() || undefined });
      if (res?.dispute) { setDone(true); setTimeout(() => onDone(res.dispute), 700); }
      else setErr("Could not open the dispute.");
    } catch (e: any) { setErr(e?.message || "Could not open the dispute."); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/50 backdrop-blur-sm" onClick={onClose}>
      <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-extrabold text-navy flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-brand-red" />Open a dispute</h3>
          <button onClick={onClose} className="btn-ghost h-8 w-8 p-0"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-10 h-10 text-brand-green mx-auto mb-2" />
            <div className="font-bold text-navy">Dispute opened</div>
            <p className="text-sm text-mut mt-1">The deal is frozen and an admin will review it.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-mut mb-3">Opening a dispute on <span className="font-semibold text-navy">{deal.contextLabel || "this deal"}</span> freezes the escrow until an admin resolves it.</p>
            <label className="label">Reason</label>
            <select className="field mb-3" value={reason} onChange={(e) => setReason(e.target.value)}>
              {Object.entries(REASON_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <label className="label">What happened?</label>
            <textarea className="field h-24 py-2" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe the issue so the admin can resolve it fairly…" maxLength={2000} />
            {err && <p className="text-sm text-brand-red flex items-center gap-1 mt-2"><AlertCircle className="w-4 h-4" />{err}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="btn-outline">Cancel</button>
              <button onClick={submit} disabled={busy} className="btn-red disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}Open dispute</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Respond to a dispute opened against you. */
export function RespondModal({ dispute, onClose, onDone }: { dispute: any; onClose: () => void; onDone: () => void }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit() {
    setErr(null); setBusy(true);
    try { await api.respondDispute(dispute.id, message.trim() || undefined); onDone(); }
    catch (e: any) { setErr(e?.message || "Could not submit your response."); setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/50 backdrop-blur-sm" onClick={onClose}>
      <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-extrabold text-navy">Respond to dispute</h3>
          <button onClick={onClose} className="btn-ghost h-8 w-8 p-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-sm bg-navy-50 rounded-lg p-3 mb-3">
          <div className="text-xs text-mut">{REASON_LABELS[dispute.reason] || dispute.reason} · {dispute.contextLabel}</div>
          {dispute.message && <p className="text-navy-700 mt-1">“{dispute.message}”</p>}
        </div>
        <label className="label">Your side of the story</label>
        <textarea className="field h-24 py-2" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add context or evidence for the admin…" maxLength={2000} />
        {err && <p className="text-sm text-brand-red flex items-center gap-1 mt-2"><AlertCircle className="w-4 h-4" />{err}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-outline">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-navy disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Submit response</button>
        </div>
      </div>
    </div>
  );
}
